import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, deleteScrapie, getScrapies } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import { SecondaryBackgroundButton } from "../../components/Buttons.js";
import {
    registrationCodeHeaderNoClick,
    birthDateHeaderNotFilterable,
    sexHeaderNoFilter,
    sampleNumberHeader,
    firstResultHeader,
    secondResultHeader,
    sampleDateHeader,
    resultDateHeader,
} from "../../components/TableHeaders.js";

const DeleteScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [fetchedScrapies, setFetchedScrapies] = useState([]);
    const [scrapiesDropdownValues, setScrapiesDropdownValues] = useState([]);
    const [selectedScrapie, setSelectedScrapie] = useState(null);
    const [step, setStep] = useState(1);
    const [scrapieCds, setScrapieCds] = useState([]);

    const selectClasses = useSelectStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const scrapiesFormContent = [
        {
            customType: "dropdown",
            id: "scrapie",
            items: scrapiesDropdownValues,
            placeholder: "Επιλογή Ζώου",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Εύρεση",
        },
    ];

    const scrapieTableColumns = [
        registrationCodeHeaderNoClick,
        birthDateHeaderNotFilterable,
        sexHeaderNoFilter,
        sampleNumberHeader,
        firstResultHeader,
        secondResultHeader,
        sampleDateHeader,
        resultDateHeader,
    ]
    const handleOwnerSelect = async (values) => {
        setIsLoading(true);
        const selectedOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        const { success: scs, scrapies, scrapieCodes, message } = await getScrapies(selectedOwner?._id);
        if (scs) {
            setFetchedScrapies(scrapies);
            setScrapiesDropdownValues(
                scrapies.map((scrapie) => ({
                    value: `${scrapie?.registrationCode}`,
                    text: `${scrapie?.registrationCode}`,
                }))
            );
            setScrapieCds(scrapieCodes);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleScrapieSelect = (values) => {
        const selectedScrapieObj = fetchedScrapies.find((scrapie) => `${scrapie?.registrationCode}` === values.scrapie);
        const tableScrapieData = {
            _id: selectedScrapieObj?._id,
            registrationCode: selectedScrapieObj?.animalId?.registrationCode,
            birthDate: selectedScrapieObj?.animalId?.birthDate,
            sex: selectedScrapieObj?.animalId?.sex,
            sampleCode: selectedScrapieObj?.sampleCode,
            sampleDate: selectedScrapieObj?.sampleDate,
            resultDate: selectedScrapieObj?.resultDate,
            result1: scrapieCds.find((code) => Number(code?.scrapieCode) === selectedScrapieObj?.result1)?.scrapieName,
            result2: scrapieCds.find((code) => Number(code?.scrapieCode) === selectedScrapieObj?.result2)?.scrapieName,
        };
        setSelectedScrapie(tableScrapieData);
    };

    const handleDeleteScrapie = async (scrapieId) => {
        setIsLoading(true);
        const { success: scs, message } = await deleteScrapie(scrapieId);
        if (scs) {
            success(message);
            const remainingScrapies = fetchedScrapies.filter((scrapie) => scrapie._id !== scrapieId);
            setFetchedScrapies(remainingScrapies);
            setScrapiesDropdownValues(
                remainingScrapies.map((scrapie) => ({
                    value: `${scrapie?.registrationCode}`,
                    text: `${scrapie?.registrationCode}`,
                }))
            );
            setSelectedScrapie(null);
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή που σας ενδιαφέρει:
                        </Typography>
                        <Form
                            content={ownerFormContent}
                            onSubmit={handleOwnerSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <>
                    <Form
                        content={scrapiesFormContent}
                        onSubmit={handleScrapieSelect}
                        toResetForm={true}
                    />
                    {selectedScrapie && (
                        <Grid2 container spacing={2} sx={{ marginTop: "1rem", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", mt: "2rem" }}>
                            <Grid2 item xs={12} md={12}>
                                <Table
                                    data={[selectedScrapie]}
                                    noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν πληροφορίες για το πρόβατο"}
                                    columns={scrapieTableColumns}
                                    showPageSizeOptions={false}
                                />
                            </Grid2>
                            {step !== 1 && (
                                <Box sx={{ width: "100%", marginTop: null, display: "flex", justifyContent: "center" }}>
                                    <SecondaryBackgroundButton
                                        variant="outlined"
                                        onClick={() => handleDeleteScrapie(selectedScrapie?._id)}
                                        title="Διαφραφή"
                                    />
                                </Box>
                            )}
                        </Grid2>
                    )}
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: "2rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    );
}

export default memo(DeleteScrapie);